import { useState, useMemo, useEffect, useCallback } from "react";
import { X, User, Building2, Shield, Smartphone } from "lucide-react";
import { IosActivityIndicator } from "@/features/auth/components/IosActivityIndicator";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { getRoleDisplayName } from "@/constants/roles";
import { loadRoleConfigLookup } from "@/features/auth/services/rolesConfigApi";
import { getFallbackRoleType } from "@/features/auth/utils/roleConfig";
import {
  completeRoleChange,
  navigateAfterRoleChange,
} from "@/features/auth/utils/postLoginFlow";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { sendAnalytics } from "@/lib/utils/analytics";
import { logoutAndRedirect } from "@/lib/api/authService";
import { cn } from "@/lib/utils/helpers";
import { OtpCodeInput } from "@/features/auth/components/OtpCodeInput";
import { OTP_LENGTH } from "@/features/auth/constants/otp";
import { useOtpCountdown } from "@/features/auth/hooks/useOtpCountdown";
import { maskPhoneForOtp } from "@/features/auth/utils/maskPhoneForOtp";
import { ROLE_RENTER, type UserRole, type ChangeRoleResponse } from "@/types/auth";
import type {
  RoleSelectionDialogProps,
  RoleConfigLookup,
  RoleSelectionAccessKey,
  RoleSelectionAccessSection,
} from "@/features/auth/types";

function resolveAccessSection(
  role: UserRole,
  typeByRoleName: Record<string, string>,
): RoleSelectionAccessKey {
  // Resident is the only role in the dedicated "Other" section.
  if (role.role_name === ROLE_RENTER) return "other";

  // Prefer scoped names used in the role label (Angular parity).
  if (role.property_name) return "property";
  if (role.company_name) return "company";

  const category =
    typeByRoleName[role.role_name] || getFallbackRoleType(role.role_name);

  if (
    category.includes("Compan") ||
    category === "Company Accounts" ||
    category === "Companies"
  ) {
    return "company";
  }
  if (category.includes("Propert") || category === "Properties") {
    return "property";
  }
  return "accounts";
}

function isRoleInactive(role: UserRole): boolean {
  if (role.property_name) {
    return role.property_listed === "0" || role.prop_listed === "0";
  }
  if (role.company_name) {
    return role.company_listed === "0" || role.com_listed === "0";
  }
  return false;
}

/**
 * Role Selection Dialog - mirrors Angular's UserRoleSelectionComponent behavior
 * with the Choose-where-to-continue layout for account / company / property access.
 */
export function RoleSelectionDialog({
  isOpen,
  onClose,
  fromLogin = false,
  pendingRole = null,
  onRoleChangeSuccess,
}: RoleSelectionDialogProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const [processing, setProcessing] = useState(false);
  const [otpResponse, setOtpResponse] = useState<ChangeRoleResponse | null>(
    null,
  );
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [otp, setOtp] = useState("");
  // Mirrors Angular FormControl touched state for OTP (red border when touched + empty)
  const [otpTouched, setOtpTouched] = useState(false);
  const [roleConfigLookup, setRoleConfigLookup] = useState<RoleConfigLookup>({
    titleByRoleName: {},
    typeByRoleName: {},
  });

  const currentRole = user?.current_role;
  const otpStepActive = Boolean(
    isOpen && otpResponse && !otpResponse.allow_role_change,
  );
  const showOtpForm = otpStepActive && !processing;
  const otpValid = otp.length === OTP_LENGTH;
  const otpInvalid = otpTouched && !otpValid;
  const {
    formatted: otpExpiresIn,
    expired: otpExpired,
    restart: restartOtpCountdown,
  } = useOtpCountdown(otpStepActive);

  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset dialog state when it closes
      setOtpResponse(null);
      setSelectedRole(null);
      setOtp("");
      setOtpTouched(false);
      setProcessing(false);
    }
  }, [isOpen]);

  // No default role selection — user must pick (Angular parity). Only restore
  // pendingRole when resuming an in-progress OTP role change from the sidebar.
  useEffect(() => {
    if (!isOpen || !pendingRole || fromLogin) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resume OTP step from temp_userrole (sidebar only)
    setSelectedRole(pendingRole);
    setOtpResponse({
      allow_role_change: false,
      message: "Enter the OTP sent to your email or phone.",
    });
  }, [fromLogin, isOpen, pendingRole]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    loadRoleConfigLookup()
      .then((lookup) => {
        if (!cancelled) {
          setRoleConfigLookup(lookup);
        }
      })
      .catch(() => {
        // Keep static getRoleDisplayName fallback if /roles cannot be loaded.
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const accessSections: RoleSelectionAccessSection[] = useMemo(() => {
    if (!user?.roles) return [];

    const buckets: Record<RoleSelectionAccessKey, UserRole[]> = {
      accounts: [],
      company: [],
      property: [],
      other: [],
    };

    for (const role of Object.values(user.roles)) {
      buckets[resolveAccessSection(role, roleConfigLookup.typeByRoleName)].push(
        role,
      );
    }

    const sections: RoleSelectionAccessSection[] = [
      {
        key: "accounts",
        title: "Liberty Rent Accounts",
        roles: buckets.accounts,
      },
      {
        key: "company",
        title: "Company Access",
        roles: buckets.company,
      },
      {
        key: "property",
        title: "Property Access",
        roles: buckets.property,
      },
      {
        key: "other",
        title: "Other",
        roles: buckets.other,
      },
    ];

    return sections.filter((section) => section.roles.length > 0);
  }, [roleConfigLookup.typeByRoleName, user?.roles]);

  const getRoleTitle = (role: UserRole): string => {
    return (
      roleConfigLookup.titleByRoleName[role.role_name] ||
      getRoleDisplayName(role.role_name)
    );
  };

  const getRoleLabel = (role: UserRole): string => {
    const title = getRoleTitle(role);
    if (role.property_name) return `${role.property_name} — ${title}`;
    if (role.company_name) return `${role.company_name} — ${title}`;
    return title;
  };

  const isCurrentRole = (role: UserRole): boolean => {
    return currentRole?.role_id === role.role_id;
  };

  const finishRoleChange = useCallback(
    async (role: UserRole) => {
      onClose();
      if (onRoleChangeSuccess) {
        await onRoleChangeSuccess(role);
      } else {
        await navigateAfterRoleChange(role, navigate, dispatch);
      }
    },
    [dispatch, navigate, onClose, onRoleChangeSuccess],
  );

  const handleRoleChange = async (role: UserRole, otpCode?: string) => {
    if (!user) return;

    setProcessing(true);
    setSelectedRole(role);

    try {
      const result = await completeRoleChange(role, user, dispatch, otpCode);

      if (result.status === "otp_required") {
        showSuccess(result.response.message || "OTP sent");
        setOtpResponse(result.response);
        setOtp("");
        setOtpTouched(false);
        setProcessing(false);
        restartOtpCountdown();
        return;
      }

      showSuccess("Role Changed");
      sendAnalytics('click', 'role change');
      await finishRoleChange(result.role);
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: string }).message)
          : "Failed to change role";
      showError(message, false);
      setProcessing(false);
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error as { status: number }).status === 401
      ) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOtpTouched(true);
    if (!selectedRole || !otpValid || processing || otpExpired) return;
    handleRoleChange(selectedRole, otp);
  };

  const handleResendOtp = () => {
    if (!selectedRole || processing) return;
    setOtp("");
    setOtpTouched(false);
    handleRoleChange(selectedRole);
  };

  const handleLogout = () => {
    onClose();
    void logoutAndRedirect();
  };

  const handleContinue = () => {
    if (!selectedRole || processing) return;
    setOtpResponse(null);
    void handleRoleChange(selectedRole);
  };

  const handleCancel = () => {
    if (processing) return;
    if (fromLogin) {
      handleLogout();
      return;
    }
    onClose();
  };

  if (!isOpen || !user) return null;

  const accountsSection = accessSections.find((s) => s.key === "accounts");
  const companySection = accessSections.find((s) => s.key === "company");
  const propertySection = accessSections.find((s) => s.key === "property");
  const otherSection = accessSections.find((s) => s.key === "other");
  const hasSplitLayout = Boolean(
    companySection || propertySection || otherSection,
  );

  // Bare centered spinner — no white dialog panel behind it
  if (processing) {
    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid="role-selection-processing"
      >
        <IosActivityIndicator
          size="md"
          label="Loading…"
          data-testid="role-selection-processing-spinner"
        />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
      onClick={fromLogin ? undefined : onClose}
    >
      <div
        className={cn(
          "flex w-full max-w-[calc(100vw-24px)] flex-col overflow-hidden bg-white shadow-xl",
          // Cap height for long lists; height itself stays content-driven
          "max-h-[min(85vh,calc(100dvh-2rem))]",
          showOtpForm
            ? "rounded-xl sm:max-w-[420px]"
            : hasSplitLayout
              ? "rounded-xl border border-gray-200 sm:max-w-4xl"
              : "rounded-xl border border-gray-200 sm:max-w-lg",
        )}
        onClick={(e) => e.stopPropagation()}
      >
          <>
            {showOtpForm && (
              <div className="relative px-6 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-6">
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded p-1 text-gray-400 transition-colors hover:text-gray-700"
                  onClick={handleCancel}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="mb-5 flex justify-center">
                  <span
                    className="relative flex h-14 w-14 items-center justify-center rounded-full bg-teal text-white"
                    aria-hidden
                  >
                    <Shield className="h-8 w-8" strokeWidth={1.75} />
                    <Smartphone className="absolute h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                </div>

                <div className="mb-6 text-center">
                  <h2 className="text-xl font-bold text-teal-dark sm:text-2xl">
                    Verify your phone
                  </h2>
                  <p className="mt-2 text-sm text-gray-600 sm:text-[0.9375rem]">
                    Enter the 6-digit code sent to
                  </p>
                  <p className="mt-1 text-base font-bold text-teal-dark sm:text-lg">
                    {maskPhoneForOtp(user.mobile_no)}
                  </p>
                </div>

                <form noValidate onSubmit={handleOtpSubmit} className="space-y-5">
                  <div>
                    <OtpCodeInput
                      value={otp}
                      onChange={(next) => {
                        setOtp(next);
                        if (next.length === OTP_LENGTH) setOtpTouched(true);
                      }}
                      disabled={processing}
                      invalid={otpInvalid}
                      autoFocus
                      aria-label="6-digit verification code"
                    />
                    <p
                      className={cn(
                        "mt-3 text-center text-sm",
                        otpExpired ? "font-medium text-red-600" : "text-gray-600",
                      )}
                      aria-live="polite"
                    >
                      {otpExpired ? (
                        "Code expired. Please resend a new code."
                      ) : (
                        <>
                          Code expires in{" "}
                          <span className="font-semibold text-[#e85d3b]">
                            {otpExpiresIn}
                          </span>
                        </>
                      )}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={processing || !otpValid || otpExpired}
                    className="w-full rounded-md bg-teal-dark px-6 py-3.5 text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-teal-darker disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Verify &amp; Continue
                  </button>

                  <div className="flex flex-col items-center pt-1">
                    <p className="text-sm text-gray-600">
                      Didn&apos;t receive the code?
                    </p>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={processing}
                      className="mt-1 text-sm font-medium text-teal underline underline-offset-2 hover:text-teal-dark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Resend code
                    </button>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mt-8 text-sm font-medium text-teal underline underline-offset-2 hover:text-teal-dark"
                    >
                      Logout
                    </button>
                  </div>
                </form>
              </div>
            )}

            {(!otpResponse || otpResponse.allow_role_change) && (
              <>
                <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-5 pb-4 pt-5 sm:px-6">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-teal-dark sm:text-2xl">
                      Choose where to continue
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Select an account, company, or property to open.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded p-1 text-gray-400 transition-colors hover:text-gray-700"
                    onClick={handleCancel}
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 sm:px-6">
                  <div
                    className={cn(
                      "grid gap-4",
                      hasSplitLayout && "lg:grid-cols-[1.1fr_1fr] lg:items-start",
                    )}
                  >
                    {accountsSection && (
                      <RoleAccessSection
                        section={accountsSection}
                        selectedRoleId={selectedRole?.role_id}
                        getRoleLabel={getRoleLabel}
                        isCurrentRole={isCurrentRole}
                        onSelect={setSelectedRole}
                        icon="user"
                        className={hasSplitLayout ? "lg:row-span-2" : undefined}
                      />
                    )}

                    <div className="flex flex-col gap-4">
                      {companySection && (
                        <RoleAccessSection
                          section={companySection}
                          selectedRoleId={selectedRole?.role_id}
                          getRoleLabel={getRoleLabel}
                          isCurrentRole={isCurrentRole}
                          onSelect={setSelectedRole}
                          icon="building"
                          maxVisibleRoles={4}
                        />
                      )}
                      {propertySection && (
                        <RoleAccessSection
                          section={propertySection}
                          selectedRoleId={selectedRole?.role_id}
                          getRoleLabel={getRoleLabel}
                          isCurrentRole={isCurrentRole}
                          onSelect={setSelectedRole}
                          icon="building"
                          maxVisibleRoles={4}
                        />
                      )}
                      {otherSection && (
                        <RoleAccessSection
                          section={otherSection}
                          selectedRoleId={selectedRole?.role_id}
                          getRoleLabel={getRoleLabel}
                          isCurrentRole={isCurrentRole}
                          onSelect={setSelectedRole}
                          icon="user"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 px-5 py-4 sm:px-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-md border-2 border-teal-dark bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-teal-dark transition-colors hover:bg-teal-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!selectedRole || processing}
                    className="rounded-md bg-teal-dark px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-teal-darker disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </>
            )}
          </>
      </div>
    </div>
  );
}

/** ~4 role rows: p-2 + (py-2.5 + h-8) per row + space-y-1.5 gaps */
const ROLE_LIST_MAX_HEIGHT_FOR_ROWS = (rows: number) =>
  `calc(1rem + ${rows} * 3.25rem + ${Math.max(0, rows - 1)} * 0.375rem)`

function RoleAccessSection({
  section,
  selectedRoleId,
  getRoleLabel,
  isCurrentRole,
  onSelect,
  icon,
  className,
  maxVisibleRoles,
}: {
  section: RoleSelectionAccessSection
  selectedRoleId?: number
  getRoleLabel: (role: UserRole) => string
  isCurrentRole: (role: UserRole) => boolean
  onSelect: (role: UserRole) => void
  icon: "user" | "building"
  className?: string
  /** When set and list is longer, only this many rows show; rest scrolls. */
  maxVisibleRoles?: number
}) {
  const constrainList =
    typeof maxVisibleRoles === "number" &&
    maxVisibleRoles > 0 &&
    section.roles.length > maxVisibleRoles

  return (
    <section
      className={cn(
        "flex flex-col rounded-xl border border-gray-200 bg-white",
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-teal-dark sm:text-base">
          {section.title}
        </h3>
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-100 px-1.5 text-xs font-semibold text-sky-700">
          {section.roles.length}
        </span>
      </div>

      <div
        className={cn(
          "space-y-1.5 p-2",
          constrainList && "overflow-y-auto overscroll-contain",
        )}
        style={
          constrainList && maxVisibleRoles
            ? { maxHeight: ROLE_LIST_MAX_HEIGHT_FOR_ROWS(maxVisibleRoles) }
            : undefined
        }
        role="radiogroup"
        aria-label={section.title}
      >
        {section.roles.map((role) => {
          const selected = selectedRoleId === role.role_id
          const inactive = isRoleInactive(role)

          return (
            <button
              key={role.role_id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(role)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                selected
                  ? "bg-emerald-50 ring-1 ring-emerald-200"
                  : "hover:bg-gray-50",
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2",
                  selected ? "border-teal-dark" : "border-gray-300",
                )}
                aria-hidden
              >
                {selected ? (
                  <span className="h-2 w-2 rounded-full bg-teal-dark" />
                ) : null}
              </span>

              <span
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-dark"
                aria-hidden
              >
                {icon === "building" ? (
                  <Building2 className="h-4 w-4" />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </span>

              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block text-sm leading-snug",
                    selected
                      ? "font-semibold text-teal-dark"
                      : "font-medium text-gray-800",
                    isCurrentRole(role) && !selected && "text-teal-dark",
                  )}
                >
                  {getRoleLabel(role)}
                </span>
                {inactive ? (
                  <span className="mt-0.5 block text-xs text-red-600">
                    (Inactive)
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
